import React from 'react';
import { GiftIcon } from 'lucide-react';
import { bonuses, problemCards, volumes } from '../data/content';
import { HeroSection } from '../components/HeroSection';
import { ProblemCard } from '../components/ProblemCard';
import { ProductIntro } from '../components/ProductIntro';
import { VolumeCard } from '../components/VolumeCard';
import { InsideLookSection } from '../components/InsideLookSection';
import { BonusCard } from '../components/BonusCard';
import { BenefitsGrid } from '../components/BenefitsGrid';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AudienceSection } from '../components/AudienceSection';
import { CtaButton } from '../components/CtaButton';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { FAQAccordion } from '../components/FAQAccordion';
import { FinalCTA } from '../components/FinalCTA';
import { Footer } from '../components/Footer';
import { PurchasePopup } from '../components/PurchasePopup';
type LandingPageProps = {
  guaranteeEnabled: boolean;
  testimonialsEnabled: boolean;
  bonusReferencePricesEnabled: boolean;
  salesPopupEnabled: boolean;
};
export function LandingPage({
  guaranteeEnabled,
  testimonialsEnabled,
  bonusReferencePricesEnabled,
  salesPopupEnabled
}: LandingPageProps) {
  return <div className="min-h-screen w-full bg-outer font-body" data-id="element-231" data-mp="3i9eyzenyzzhp">
      <main className="mx-auto w-full max-w-[768px] overflow-hidden bg-white" data-id="element-232" data-mp="3i8wq9ik4fssw">
        <HeroSection guaranteeEnabled={guaranteeEnabled} data-id="element-233" data-mp="3n7gi1zlebyt6" />

        <section className="space-y-2.5 bg-surface px-4 py-4 md:px-12 md:py-6" data-id="element-234" data-mp="3ra39hze5nhpu">
          {problemCards.map(c => <ProblemCard key={c.title} {...c} data-id="element-235" data-mp="4blb5jxuf5o" />)}
        </section>

        <ProductIntro data-id="element-236" data-mp="1ww3jgy0hvaeg" />

        <section className="bg-white px-4 pb-6 md:px-12 md:pb-8" data-id="element-237" data-mp="1wwveo7q6j647">
          <h2 className="text-center font-heading text-[24px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-navy md:text-[32px]" data-id="element-238" data-mp="21v6nqg8nhcld">
            What's Included
          </h2>
          <div className="mt-3 space-y-2.5" data-id="element-239" data-mp="357x7m08n08rr">
            {volumes.map(({
            number,
            ...v
          }) => <VolumeCard key={number} label={`Volume ${number}`} {...v} data-id="element-240" data-mp="309c67xhnfj1q" />)}
          </div>
          <div className="mt-5 text-center" data-id="element-241" data-mp="3e4eayharf2cy">
            <p className="font-heading text-[14px] font-extrabold uppercase leading-tight text-navy md:text-[16px]" data-id="element-242" data-mp="396mhhiugv86n">
              Stop rebuilding the same tables and forms from scratch.
            </p>
            <div className="mt-2.5" data-id="element-243" data-mp="3j4slc5zu2oe5">
              <CtaButton mode="scroll" subtext="All 6 volumes + 5 free bonuses for only $26.90" data-id="element-244" data-mp="3j3r5zpbsbfqm">
                Organize my clinical practice today
              </CtaButton>
            </div>
          </div>
        </section>

        <InsideLookSection data-id="element-245" data-mp="apsbks4go2f8" />

        <section className="bg-navy px-4 py-6 md:px-12 md:py-8" data-id="element-246" data-mp="ar3g4u5qde7e">
          <h2 className="flex items-center justify-center gap-2 text-center font-heading text-[21px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-gold md:text-[32px]" data-id="element-247" data-mp="5r7ayrlargq9">
            <GiftIcon className="h-6 w-6 shrink-0 md:h-8 md:w-8" strokeWidth={2.5} aria-hidden data-id="element-248" data-mp="pawbur1ge8hf" />
            Plus, get 5 professional bonuses free!
          </h2>
          <p className="mx-auto mt-1.5 max-w-[560px] text-center text-[14px] leading-[1.4] text-onNavyMuted md:text-[15px]" data-id="element-249" data-mp="fe1i0lg4kw9h">
            Five complementary resources for documentation, assessment, perioperative preparation, and patient
            communication.
          </p>
          <div className="mt-3 space-y-2.5" data-id="element-250" data-mp="bd7x80xzbxkt">
            {bonuses.map(b => <BonusCard key={b.number} {...b} showReferencePrice={bonusReferencePricesEnabled} data-id="element-251" data-mp="3stzg6tn3p8ic" />)}
          </div>
        </section>

        <BenefitsGrid data-id="element-252" data-mp="1nod6ryj5xod" />
        <OfferSection showReferencePrices={bonusReferencePricesEnabled} guaranteeEnabled={guaranteeEnabled} data-id="element-253" data-mp="1ktp8qgb5uq7" />
        {guaranteeEnabled && <GuaranteeSection data-id="element-254" data-mp="3nuw8f1fqko6o" />}
        <AudienceSection data-id="element-255" data-mp="3nt33proooe1b" />
        {testimonialsEnabled && <TestimonialsSection data-id="element-256" data-mp="3gr6i7osn4zy0" />}
        <FAQAccordion data-id="element-257" data-mp="3gq4wmqe00v21" />
        <FinalCTA guaranteeEnabled={guaranteeEnabled} data-id="element-258" data-mp="3gqn30fudkpno" />
        <Footer data-id="element-259" data-mp="3brjw0puu49d2" />
      </main>
      {salesPopupEnabled && <PurchasePopup data-id="element-260" data-mp="3bsv95ga66tgu" />}
    </div>;
}